import { useCallback, useEffect, useState } from 'react';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { VoicemailMessageAlertEvent } from '@weave/schema-gen-ts/dist/shared/phone/v1/voicemail/message.pb';
import { useVoicemailNotification } from '@frontend/notifications';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';
import { useVoicemailDetailsPanelHelper } from './voicemail-hooks';

export const useWebsocketVoicemailMessageEventSubscription = () => {
  const { selectedLocationIds } = useAppScopeStore();

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isVoicemailNotificationFeatureEnabled = getFeatureFlagValue('voicemail-notification-feature-enabled');
  const {
    show: showPanel,
    setShow,
    context,
    panelType,
  } = useSlidePanelShallowStore('setShow', 'show', 'context', 'panelType');

  const [pendingViewParams, setPendingViewParams] = useState<VoicemailMessageAlertEvent | null>(null);

  const { voicemailDetailsQuery, optimisticRemoveTag, optimisticApplyTag, updateReadStatusAsync, onDelete } =
    useVoicemailDetailsPanelHelper({
      params: pendingViewParams,
      selectedLocationIds,
      panelType,
      setShow,
    });

  useEffect(() => {
    const voicemailData = voicemailDetailsQuery.data;
    if (voicemailData) {
      const uniqueKey = `${voicemailData?.channelId}-${voicemailData?.mailboxId}-${voicemailData?.mediaId}-${voicemailData?.voicemailId}`;
      setShow(true, 'voicemailTranscription', {
        rows: [{ ...voicemailData }],
        currentRowId: uniqueKey || '',
        instanceId: null,
        toggleRowSelected: () => {},
        updateReadStatus: updateReadStatusAsync,
        deleteVoicemail: onDelete,
        voicemailsQueryKey: voicemailDetailsQuery.queryKey,
        optimisticApplyTag,
        optimisticRemoveTag,
        sectionFocus: showPanel ? (context as any)?.sectionFocus : 'transcript',
        userHasAccess: true, // users will receive notifications for new voicemails only if they have access to - its true in all cases
        prioritizeQueryCache: true,
      });
    }
  }, [voicemailDetailsQuery.data, pendingViewParams]);

  const { create: createNotification } = useVoicemailNotification({
    onView: async (event) => {
      const { payload: params } = event;
      setPendingViewParams(params);
    },
  });

  const eventHandler = useCallback<GetWebsocketEventHandler<NotificationType.VOICEMAIL_MESSAGE>>(
    (event) => {
      const { params } = event;

      if (!isVoicemailNotificationFeatureEnabled) {
        return;
      }

      createNotification({
        id: event.id,
        timestamp: Date.now(),
        type: 'voicemail-message',
        payload: params,
        state: {
          paused: false,
          status: 'unread',
        },
      });
    },
    [createNotification]
  );
  return useWebsocketEventSubscription(NotificationType.VOICEMAIL_MESSAGE, eventHandler);
};
